/**
 * Main Navigation Component
 */
class MainNavigation {
    constructor(container) {
        this.container = container;
        this.mobileToggle = container.querySelector('.main-navigation__mobile-toggle');
        this.mobileOverlay = container.querySelector('.main-navigation__mobile-overlay');
        this.mobileClose = container.querySelector('.main-navigation__mobile-close');
        this.dropdownItems = container.querySelectorAll('.main-navigation__item--has-dropdown');
        this.isMobileMenuOpen = false;
        this.lastFocusedElement = null;

        this.init();
    }

    init() {
        this.bindEvents();
        this.setupAccessibility();
    }

    bindEvents() {
        if (this.mobileToggle) {
            this.mobileToggle.addEventListener('click', () => this.toggleMobileMenu());
        }

        if (this.mobileClose) {
            this.mobileClose.addEventListener('click', () => this.closeMobileMenu());
        }

        if (this.mobileOverlay) {
            this.mobileOverlay.addEventListener('click', (e) => {
                if (e.target === this.mobileOverlay) {
                    this.closeMobileMenu();
                }
            });
        }

        this.dropdownItems.forEach((item) => {
            const link = item.querySelector('.main-navigation__link');
            const dropdown = item.querySelector('.main-navigation__dropdown');

            if (!link || !dropdown) {
                return;
            }

            item.addEventListener('mouseenter', () => this.showDropdown(dropdown, link));
            item.addEventListener('mouseleave', () => this.hideDropdown(dropdown, link));

            link.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    this.showDropdown(dropdown, link);
                } else if (e.key === 'Escape') {
                    this.hideDropdown(dropdown, link);
                }
            });

            link.addEventListener('focus', () => this.showDropdown(dropdown, link));

            document.addEventListener('click', (e) => {
                if (!item.contains(e.target)) {
                    this.hideDropdown(dropdown, link);
                }
            });
        });

        document.addEventListener('click', (e) => {
            this.dropdownItems.forEach((item) => {
                const dropdown = item.querySelector('.main-navigation__dropdown');
                const link = item.querySelector('.main-navigation__link');
                if (dropdown && !item.contains(e.target)) {
                    this.hideDropdown(dropdown, link);
                }
            });
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isMobileMenuOpen) {
                this.closeMobileMenu();
            }
        });

        window.addEventListener('resize', () => {
            this.handleResize();
        });

        let scrollTimeout;
        window.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                this.handleScroll();
            }, 100);
        });
    }

    showDropdown(dropdown, triggerLink) {
        this.dropdownItems.forEach((item) => {
            const otherDropdown = item.querySelector('.main-navigation__dropdown');
            const otherLink = item.querySelector('.main-navigation__link');
            if (otherDropdown && otherDropdown !== dropdown) {
                this.hideDropdown(otherDropdown, otherLink);
            }
        });

        dropdown.style.opacity = '1';
        dropdown.style.visibility = 'visible';
        dropdown.style.transform = 'translateY(0)';

        if (triggerLink) {
            triggerLink.setAttribute('aria-expanded', 'true');
        }
    }

    hideDropdown(dropdown, triggerLink) {
        dropdown.style.opacity = '0';
        dropdown.style.visibility = 'hidden';
        dropdown.style.transform = 'translateY(-10px)';

        if (triggerLink) {
            triggerLink.setAttribute('aria-expanded', 'false');
        }
    }

    toggleMobileMenu() {
        if (this.isMobileMenuOpen) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    openMobileMenu() {
        this.isMobileMenuOpen = true;
        this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        this.mobileOverlay?.classList.add('active');
        this.mobileToggle?.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';

        const firstMenuItem = this.mobileOverlay?.querySelector('.main-navigation__mobile-link');
        if (firstMenuItem) {
            firstMenuItem.focus();
        }
    }

    closeMobileMenu() {
        this.isMobileMenuOpen = false;
        this.mobileOverlay?.classList.remove('active');
        this.mobileToggle?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';

        if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
            this.lastFocusedElement.focus();
        }
    }

    handleResize() {
        const isMobile = window.innerWidth <= 1200;

        if (this.mobileToggle) {
            this.mobileToggle.style.display = isMobile ? 'block' : 'none';
        }

        if (!isMobile && this.isMobileMenuOpen) {
            this.closeMobileMenu();
        }

        this.dropdownItems.forEach((item) => {
            const dropdown = item.querySelector('.main-navigation__dropdown');
            const link = item.querySelector('.main-navigation__link');
            if (dropdown) {
                this.hideDropdown(dropdown, link);
            }
        });
    }

    handleScroll() {
        const scrollTop = window.pageYOffset;

        if (scrollTop > 50) {
            this.container.classList.add('scrolled');
        } else {
            this.container.classList.remove('scrolled');
        }

        this.dropdownItems.forEach((item) => {
            const dropdown = item.querySelector('.main-navigation__dropdown');
            const link = item.querySelector('.main-navigation__link');
            if (dropdown) {
                this.hideDropdown(dropdown, link);
            }
        });
    }

    setupAccessibility() {
        this.container.setAttribute('role', 'navigation');
        this.container.setAttribute('aria-label', 'Основное меню сайта');

        if (this.mobileToggle) {
            this.mobileToggle.setAttribute('aria-label', 'Открыть меню');
            this.mobileToggle.setAttribute('aria-expanded', 'false');
            this.mobileToggle.setAttribute('aria-controls', 'mobile-menu');
            this.mobileToggle.setAttribute('aria-haspopup', 'dialog');
        }

        if (this.mobileOverlay) {
            this.mobileOverlay.setAttribute('role', 'dialog');
            this.mobileOverlay.setAttribute('aria-modal', 'true');
            this.mobileOverlay.setAttribute('aria-labelledby', 'mobile-menu-title');
        }

        if (this.mobileClose) {
            this.mobileClose.setAttribute('aria-label', 'Закрыть меню');
        }

        this.dropdownItems.forEach((item, index) => {
            const link = item.querySelector('.main-navigation__link');
            const dropdown = item.querySelector('.main-navigation__dropdown');

            if (link && dropdown) {
                const itemId = `dropdown-${index}`;
                dropdown.setAttribute('id', itemId);
                link.setAttribute('aria-haspopup', 'true');
                link.setAttribute('aria-expanded', 'false');
                link.setAttribute('aria-controls', itemId);
            }
        });
    }

    closeAllDropdowns() {
        this.dropdownItems.forEach((item) => {
            const dropdown = item.querySelector('.main-navigation__dropdown');
            const link = item.querySelector('.main-navigation__link');
            if (dropdown) {
                this.hideDropdown(dropdown, link);
            }
        });
    }

    destroy() {
        if (this.mobileToggle) {
            this.mobileToggle.removeEventListener('click', this.toggleMobileMenu);
        }

        if (this.mobileClose) {
            this.mobileClose.removeEventListener('click', this.closeMobileMenu);
        }

        this.dropdownItems.forEach((item) => {
            const dropdown = item.querySelector('.main-navigation__dropdown');
            if (dropdown) {
                const link = item.querySelector('.main-navigation__link');
                if (link) {
                    link.removeAttribute('aria-controls');
                    link.removeAttribute('aria-expanded');
                    link.removeAttribute('aria-haspopup');
                }
            }
        });

        this.closeMobileMenu();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const navigation = document.querySelector('.main-navigation');
    if (navigation) {
        window.mainNavigation = new MainNavigation(navigation);

        if (window.XTronWebApp) {
            window.XTronWebApp.mainNavigation = window.mainNavigation;
        }
    }
});

if (typeof module !== 'undefined' && module.exports) {
    module.exports = MainNavigation;
}
